import { Camera, Footprints, Gauge, Timer, Unplug, Wifi } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Tile } from "@/components/challenge/challenge-parts";
import { Hint } from "@/components/run-detail";
import { useNow } from "@/hooks/use-now";
import type { Challenge, SimFrame, SimSession } from "@/lib/api";
import { studentApi } from "@/lib/api";
import { formatSeconds, formatTimeLeft } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * A session while it runs: what the robot sees, and how the run is going.
 *
 * Only the camera is shown. The robot's position on the map is ground truth the student's
 * code does not have, and drawing it live would hand a human watching the page exactly the
 * localisation the challenge asks the code to do. The frame is what the SDK already
 * receives, so showing it gives nothing away; the map appears when the run ends.
 *
 * The page polls the session, and this component fetches a frame whenever the session
 * says a newer one exists (`live.last_frame_at`), so an idle robot costs nothing.
 */
export function LiveSession({
  session,
  challenge,
  apiKey,
}: {
  session: SimSession;
  challenge: Challenge;
  apiKey: string;
}) {
  const live = session.live;
  const now = useNow(1000);
  const since = session.connected_at ?? session.started_at;
  const elapsedS = Math.max(0, (now - since) / 1000);
  const used = live?.steps_used ?? session.steps_used;
  const budget = challenge.max_steps;
  const share = Math.min(100, (used / budget) * 100);
  const frameAgeS =
    live?.last_frame_at == null ? null : Math.max(0, (now - live.last_frame_at) / 1000);
  const detached = live !== null && !live.connected;

  return (
    <div className="space-y-4">
      {detached ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-600/40 bg-amber-500/10 px-4 py-3 text-sm"
        >
          <Unplug className="size-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Disconnected — the run is waiting</p>
            <p className="text-muted-foreground text-xs">
              The SDK lost its connection. The robot is holding still with {used.toLocaleString()}{" "}
              steps used; the SDK reconnects by itself, and the run continues from here. If nobody
              comes back in time, the attempt ends unscored.
            </p>
          </div>
          {live.grace_ends_at !== null ? (
            <span className="font-mono text-sm tabular-nums">
              {formatTimeLeft(live.grace_ends_at, now)} left
            </span>
          ) : null}
        </div>
      ) : null}

    <div className="grid gap-6 lg:grid-cols-[26rem_minmax(0,1fr)]">
      <FrameView
        sessionId={session.id}
        apiKey={apiKey}
        frameAt={live?.last_frame_at ?? null}
      />

      <div className="grid content-start grid-cols-2 gap-3">
        <Tile icon={<Footprints className="size-3.5" aria-hidden />} label="Steps">
          <span className="font-mono">{used.toLocaleString()}</span>
          <span className="text-muted-foreground font-normal">
            {" / "}
            {budget.toLocaleString()}
          </span>
          <span
            className="bg-muted mt-2 block h-1.5 w-full overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={used}
            aria-valuemin={0}
            aria-valuemax={budget}
          >
            <span
              className={cn("bg-primary block h-full rounded-full transition-[width]", share > 90 && "bg-destructive")}
              style={{ width: `${share}%` }}
            />
          </span>
          <Hint>
            {live ? `${live.steps_left.toLocaleString()} left` : "budget for this run"}
          </Hint>
        </Tile>

        <Tile icon={<Gauge className="size-3.5" aria-hidden />} label="Thinking">
          <span className="font-mono">
            {live?.think_mean_ms == null ? "—" : `${live.think_mean_ms.toFixed(0)} ms`}
          </span>
          <Hint>per step, your code, as we measure it</Hint>
        </Tile>

        <Tile icon={<Timer className="size-3.5" aria-hidden />} label="Elapsed">
          <span className="font-mono">{formatSeconds(elapsedS)}</span>
          <Hint>{formatSeconds(used / 100)} of robot time</Hint>
        </Tile>

        <Tile icon={<Wifi className="size-3.5" aria-hidden />} label="Link">
          <span className="font-mono">
            {live === null ? "—" : live.connected ? "connected" : "dropped"}
          </span>
          <Hint>
            {frameAgeS === null
              ? "no frame yet"
              : frameAgeS < 2
                ? "frame just now"
                : `last frame ${formatSeconds(frameAgeS)} ago`}
          </Hint>
        </Tile>
        <p className="text-muted-foreground col-span-2 text-xs">
          Refreshes every second while the run is going. The path appears here once it
          ends — calling <code className="font-mono">checkin()</code> scores it; losing
          the connection pauses it for five minutes, and Abort ends it.
        </p>
      </div>
    </div>
    </div>
  );
}

/**
 * The newest camera frame, refetched when `frameAt` changes.
 *
 * JPEG frames become an object URL on an `<img>`; raw ones are painted onto a canvas from
 * their bytes, since a worker may send either and the gateway forwards what it got.
 */
function FrameView({
  sessionId,
  apiKey,
  frameAt,
}: {
  sessionId: string;
  apiKey: string;
  frameAt: number | null;
}) {
  const [shown, setShown] = useState<Shown | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (frameAt === null) return;
    let cancelled = false;
    studentApi
      .simFrame(sessionId, apiKey)
      .then((next) => {
        if (cancelled || !next) return;
        setShown(
          next.encoding === "jpeg"
            ? { encoding: "jpeg", url: URL.createObjectURL(next.blob) }
            : next,
        );
      })
      .catch(() => {
        // A missed frame is not worth an error state; the next tick tries again.
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, apiKey, frameAt]);

  // Release the previous object URL once a newer frame has replaced it.
  useEffect(
    () => () => {
      if (shown?.encoding === "jpeg") URL.revokeObjectURL(shown.url);
    },
    [shown],
  );

  useEffect(() => {
    if (shown?.encoding !== "raw") return;
    const [h, w, c] = shown.shape;
    const ctx = canvas.current?.getContext("2d");
    if (!ctx) return;
    const rgba = new Uint8ClampedArray(w * h * 4);
    // RGB, or a single grey channel replicated.
    for (let i = 0, j = 0; i < w * h; i++, j += 4) {
      const k = i * c;
      rgba[j] = shown.data[k];
      rgba[j + 1] = shown.data[c >= 3 ? k + 1 : k];
      rgba[j + 2] = shown.data[c >= 3 ? k + 2 : k];
      rgba[j + 3] = 255;
    }
    ctx.canvas.width = w;
    ctx.canvas.height = h;
    ctx.putImageData(new ImageData(rgba, w, h), 0, 0);
  }, [shown]);

  return (
    <figure className="min-w-0 space-y-2">
      <div className="bg-muted/40 relative aspect-[4/3] w-full max-w-[26rem] overflow-hidden rounded-lg border">
        {shown?.encoding === "jpeg" ? (
          <img
            src={shown.url}
            alt="What the robot's camera sees right now"
            className="size-full object-contain"
          />
        ) : shown?.encoding === "raw" ? (
          <canvas
            ref={canvas}
            aria-label="What the robot's camera sees right now"
            className="size-full object-contain [image-rendering:pixelated]"
          />
        ) : (
          <div className="text-muted-foreground absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm">
            <Camera className="size-5" aria-hidden />
            Waiting for the first frame…
          </div>
        )}
      </div>
      <figcaption className="text-muted-foreground text-xs">
        The robot's camera. The map stays hidden while the run is live.
      </figcaption>
    </figure>
  );
}

/** A frame ready to display: a JPEG as an object URL, or raw bytes for the canvas. */
type Shown = { encoding: "jpeg"; url: string } | Extract<SimFrame, { encoding: "raw" }>;
